import React, { useState } from 'react';
import './EquipmentIcon.css';

interface EquipmentIconProps {
  imageUrl?: string;
  iconName?: string;
  itemName: string;
  itemType: 'Weapon' | 'Armor' | 'Accessory';
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
  stats?: {
    attackBonus?: number;
    defenseBonus?: number;
    hpBonus?: number;
    speedBonus?: number;
  };
  className?: string;
}

const EquipmentIcon: React.FC<EquipmentIconProps> = ({
  imageUrl,
  iconName,
  itemName,
  itemType,
  size = 'medium',
  showTooltip = false,
  stats,
  className = ''
}) => {
  const [imageError, setImageError] = useState(false);
  const [showTooltipState, setShowTooltipState] = useState(false);

  // Generate fallback icon based on item type
  const getFallbackIcon = (): string => {
    const typeIcons: Record<string, string> = {
      'Weapon': '⚔️',
      'Armor': '🛡️',
      'Accessory': '💍'
    };
    return typeIcons[itemType] || '📦';
  };

  // Generate fallback color based on item type
  const getTypeColor = (): string => {
    const typeColors: Record<string, string> = {
      'Weapon': '#e74c3c',
      'Armor': '#3498db', 
      'Accessory': '#9b59b6'
    };
    return typeColors[itemType] || '#95a5a6';
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const shouldShowImage = imageUrl && !imageError;
  const sizeClass = `equipment-icon-${size}`;
  
  return (
    <div 
      className={`equipment-icon ${sizeClass} ${className}`}
      onMouseEnter={() => showTooltip && setShowTooltipState(true)}
      onMouseLeave={() => showTooltip && setShowTooltipState(false)}
    >
      <div className="equipment-icon-container">
        {shouldShowImage ? (
          <img
            src={imageUrl}
            alt={itemName}
            className="equipment-image"
            onError={handleImageError}
          />
        ) : (
          <div 
            className="equipment-fallback"
            style={{ 
              backgroundColor: getTypeColor(),
              color: 'white'
            }}
          >
            {getFallbackIcon()}
          </div>
        )}
        
        {/* Item type indicator */}
        <div className={`equipment-type-indicator ${itemType.toLowerCase()}`}>
          {itemType.charAt(0)}
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && showTooltipState && (
        <div className="equipment-tooltip">
          <div className="tooltip-header">
            <h4>{itemName}</h4>
            <span className="tooltip-type">{itemType}</span>
          </div>
          
          {stats && (
            <div className="tooltip-stats">
              {stats.attackBonus !== undefined && stats.attackBonus > 0 && (
                <div className="stat-line">
                  <span className="stat-label">Attack:</span>
                  <span className="stat-value">+{stats.attackBonus}</span>
                </div>
              )}
              {stats.defenseBonus !== undefined && stats.defenseBonus > 0 && (
                <div className="stat-line">
                  <span className="stat-label">Defense:</span>
                  <span className="stat-value">+{stats.defenseBonus}</span>
                </div>
              )}
              {stats.hpBonus !== undefined && stats.hpBonus > 0 && (
                <div className="stat-line">
                  <span className="stat-label">HP:</span>
                  <span className="stat-value">+{stats.hpBonus}</span>
                </div>
              )}
              {stats.speedBonus !== undefined && stats.speedBonus > 0 && (
                <div className="stat-line">
                  <span className="stat-label">Speed:</span>
                  <span className="stat-value">+{stats.speedBonus}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EquipmentIcon;